import { useState, useEffect, Fragment } from "react";
import { HasAccess, useHasAccess } from "@/components/common/has-access";
import { format } from "date-fns";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import {
  BundleDiscrepancy,
  FLAG_CONFIG,
  isFixable,
  resolveCompletedEarlyFix,
  resolveFixStatus,
  resolveOverRecordedFix,
  resolveSessionCountFix,
} from "@/pages/audit/types/bundle-discrepancy";
import { useFixBundleDiscrepancies } from "../hooks/use-fix-bundle-discrepancies";
import { useCompletedBundleSessions } from "../hooks/use-completed-bundle-sessions";
import { useOrphanedSessions } from "../hooks/use-orphaned-sessions";
import { useIncorrectMappedSessions } from "../hooks/use-incorrect-mapped-sessions";
import { FixItem } from "../services/fix-bundle-discrepancies";
import { OrphanedSession } from "../services/get-orphaned-sessions";
import {
  IncorrectMappedSession,
  IncorrectMappingViolationReason,
} from "../services/get-incorrect-mapped-sessions";
import { AuditTimestamp } from "./audit-timestamp";

// ─── Incorrect mapped sessions panel (read-only) ───────────────────────────────

const VIOLATION_LABELS: Record<IncorrectMappingViolationReason, string> = {
  no_compatible_bundle: "Type mismatch",
  out_of_range: "Out of range",
};

function IncorrectMappedSessionRow({ session }: { session: IncorrectMappedSession }) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-800 truncate">{session.name}</p>
        <p className="text-xs text-muted-foreground">{session.status}</p>
      </div>
      <AuditTimestamp
        value={session.start_time}
        className="text-xs text-muted-foreground shrink-0"
      />
      <Badge
        variant="outline"
        className="text-xs shrink-0 bg-rose-50 text-rose-800 border-rose-200"
      >
        {VIOLATION_LABELS[session.violation_reason]}
      </Badge>
    </div>
  );
}

function IncorrectMappedSessionsPanel({ bundleUsageId }: { bundleUsageId: string }) {
  const { data, isLoading } = useIncorrectMappedSessions(bundleUsageId);
  const sessions = data?.sessions ?? [];

  if (isLoading) {
    return (
      <p className="px-3 py-2.5 text-xs text-muted-foreground">Loading sessions…</p>
    );
  }

  if (sessions.length === 0) {
    return (
      <p className="px-3 py-2.5 text-xs text-muted-foreground italic">
        No incorrectly mapped sessions found.
      </p>
    );
  }

  return (
    <div className="divide-y divide-rose-100 max-h-[200px] overflow-y-auto">
      {sessions.map((session) => (
        <IncorrectMappedSessionRow key={session.id} session={session} />
      ))}
    </div>
  );
}

// ─── Orphaned session accordion (one per over-recorded row in the dialog) ────

interface OrphanedSessionAccordionProps {
  row: BundleDiscrepancy;
  selectedSessionIds: Set<string>;
  onToggleSession: (sessionId: string) => void;
  onSetSessions: (sessionIds: string[]) => void;
  reduceUtilized: boolean;
  onToggleReduceUtilized: () => void;
}

function OrphanedSessionAccordion({
  row,
  selectedSessionIds,
  onToggleSession,
  onSetSessions,
  reduceUtilized,
  onToggleReduceUtilized,
}: OrphanedSessionAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { data, isLoading } = useOrphanedSessions(row.id);

  const sessions = data?.sessions ?? [];
  const linkable = sessions.filter((s) => !s.is_linked_elsewhere);

  // Auto-select all linkable sessions when they first load
  useEffect(() => {
    if (linkable.length > 0) {
      onSetSessions(linkable.map((s) => s.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectedCount = linkable.filter((s) => selectedSessionIds.has(s.id)).length;
  const { postActual, newStatus } = resolveOverRecordedFix(row, selectedCount);

  // Residual: compare post-link actual against the UNCHANGED utilized count
  let residualNote: string | null = null;
  if (postActual > row.utilized_session_quantity) {
    residualNote = `Actual (${postActual}) > utilized (${row.utilized_session_quantity}) — Session Count Low discrepancy will remain.`;
  } else if (postActual < row.utilized_session_quantity) {
    residualNote = `Actual (${postActual}) < utilized (${row.utilized_session_quantity}) — Session Count High discrepancy will remain.`;
  }

  return (
    <div className="rounded-md border border-yellow-200 overflow-hidden">
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-yellow-50 hover:bg-yellow-100 transition-colors"
      >
        <span className="text-xs font-medium text-yellow-900 truncate pr-2">
          {row.bundle_name}
          {isLoading && (
            <span className="ml-2 text-yellow-600 font-normal">loading…</span>
          )}
        </span>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-yellow-700" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-yellow-700" />
        )}
      </button>

      {isExpanded && (
        <div>
          {/* Session list */}
          <div className="divide-y divide-yellow-100 max-h-[180px] overflow-y-auto">
            {!isLoading && linkable.length === 0 && (
              <p className="px-3 py-2.5 text-xs text-gray-500 italic">
                No orphaned sessions found matching this user's email or phone. Manual review required.
              </p>
            )}
            {linkable.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                checked={selectedSessionIds.has(s.id)}
                onToggle={() => onToggleSession(s.id)}
              />
            ))}
          </div>

          {/* Impact summary */}
          <div className="px-3 py-2.5 bg-gray-50 border-t border-yellow-100 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                Sessions to link:{" "}
                <span className="font-medium text-gray-800">{selectedCount}</span>
              </span>
              <span>
                Post-fix actual:{" "}
                <span className="font-medium text-gray-800">{postActual}</span>
                {" / utilized: "}
                <span className="font-medium text-gray-800">{row.utilized_session_quantity}</span>
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">
                Status →{" "}
                {newStatus ? (
                  <span className={cn(
                    "font-semibold",
                    newStatus === "COMPLETED" && "text-green-700",
                    newStatus === "EXPIRED"   && "text-red-700",
                  )}>
                    {newStatus}
                  </span>
                ) : (
                  <span className="font-medium text-gray-500">no change</span>
                )}
              </span>
              {residualNote === null ? (
                <span className="text-green-700 font-medium">No residual discrepancy</span>
              ) : (
                <span className="text-amber-700 max-w-[240px] text-right">{residualNote}</span>
              )}
            </div>

            {/* Fallback: reduce utilized count when gap still remains */}
            {postActual < row.utilized_session_quantity && (
              <label className="flex items-center gap-2 pt-1 cursor-pointer select-none">
                <Checkbox
                  checked={reduceUtilized}
                  onCheckedChange={onToggleReduceUtilized}
                  className="shrink-0"
                />
                <span className="text-xs text-gray-700">
                  Reduce utilized count to match actual{" "}
                  <span className="font-medium">
                    ({row.utilized_session_quantity} → {postActual})
                  </span>
                </span>
              </label>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

interface SessionRowProps {
  session: OrphanedSession;
  checked?: boolean;
  onToggle?: () => void;
  readOnly?: boolean;
}

function SessionRow({ session, checked, onToggle, readOnly }: SessionRowProps) {
  return (
    <div className="flex items-center gap-2.5 px-3 py-2">
      {!readOnly && (
        <Checkbox
          checked={checked}
          onCheckedChange={onToggle}
          className="shrink-0"
        />
      )}
      <div className="flex-1 min-w-0">
        <p className="text-xs font-medium text-gray-800 truncate">
          {session.invitee_full_name ?? "Unknown"}
        </p>
        <p className="text-xs text-muted-foreground truncate">
          {[session.invitee_email, session.invitee_phone]
            .filter(Boolean)
            .join(" · ")}
        </p>
      </div>
      <span className="text-xs text-muted-foreground shrink-0">
        {format(new Date(session.start_time), "dd MMM yyyy")}
      </span>
    </div>
  );
}

// ─── Completed-early session accordion ───────────────────────────────────────

interface CompletedEarlySessionAccordionProps {
  row: BundleDiscrepancy;
  selectedSessionIds: Set<string>;
  onToggleSession: (sessionId: string) => void;
  onSetSessions: (sessionIds: string[]) => void;
}

function CompletedEarlySessionAccordion({
  row,
  selectedSessionIds,
  onToggleSession,
  onSetSessions,
}: CompletedEarlySessionAccordionProps) {
  const [isExpanded, setIsExpanded] = useState(true);
  const { data, isLoading } = useCompletedBundleSessions(row.id);

  const sessions = data?.sessions ?? [];
  const linkable = sessions.filter((s) => !s.is_linked_elsewhere);

  // Auto-select all linkable sessions when they first load
  useEffect(() => {
    if (linkable.length > 0) {
      onSetSessions(linkable.map((s) => s.id));
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const selectedCount = linkable.filter((s) => selectedSessionIds.has(s.id)).length;
  const { newUtilized, newStatus } = resolveCompletedEarlyFix(row, selectedCount);
  // post-fix actual = sessions already mapped + sessions about to be mapped
  const postActual = row.actual_session_count + selectedCount;

  let residualNote: string | null = null;
  if (postActual > newUtilized) {
    residualNote = `Actual (${postActual}) > utilized (${newUtilized}) — Session Count Low discrepancy will remain.`;
  } else if (postActual < newUtilized) {
    residualNote = `Actual (${postActual}) < utilized (${newUtilized}) — Session Count High discrepancy will remain.`;
  }

  return (
    <div className="rounded-md border border-purple-200 overflow-hidden">
      {/* Header */}
      <button
        onClick={() => setIsExpanded((v) => !v)}
        className="w-full flex items-center justify-between px-3 py-2.5 text-left bg-purple-50 hover:bg-purple-100 transition-colors"
      >
        <span className="text-xs font-medium text-purple-900 truncate pr-2">
          {row.bundle_name}
          {isLoading && (
            <span className="ml-2 text-purple-600 font-normal">loading…</span>
          )}
        </span>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-purple-700" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 shrink-0 text-purple-700" />
        )}
      </button>

      {isExpanded && (
        <div>
          {/* Session list */}
          <div className="divide-y divide-purple-100 max-h-[180px] overflow-y-auto">
            {!isLoading && linkable.length === 0 && (
              <p className="px-3 py-2.5 text-xs text-gray-500 italic">
                No unmapped sessions found within this bundle's date range.
              </p>
            )}
            {linkable.map((s) => (
              <SessionRow
                key={s.id}
                session={s}
                checked={selectedSessionIds.has(s.id)}
                onToggle={() => onToggleSession(s.id)}
              />
            ))}
          </div>

          {/* Impact summary */}
          <div className="px-3 py-2.5 bg-gray-50 border-t border-purple-100 space-y-1.5">
            <div className="flex items-center justify-between text-xs text-gray-600">
              <span>
                Sessions to map:{" "}
                <span className="font-medium text-gray-800">{selectedCount}</span>
              </span>
              <span>
                New utilized:{" "}
                <span className="font-medium text-gray-800">{newUtilized}</span>
                {" / total: "}
                <span className="font-medium text-gray-800">{row.total_session_quantity}</span>
              </span>
            </div>
            <div className="flex items-center justify-between text-xs">
              <span className="text-gray-600">
                Status →{" "}
                <span className={cn(
                  "font-semibold",
                  newStatus === "COMPLETED" && "text-green-700",
                  newStatus === "EXPIRED"   && "text-red-700",
                  newStatus === "ACTIVE"    && "text-blue-700",
                )}>
                  {newStatus}
                </span>
              </span>
              {residualNote === null ? (
                <span className="text-green-700 font-medium">No residual discrepancy</span>
              ) : (
                <span className="text-amber-700 max-w-[240px] text-right">{residualNote}</span>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Main table component ─────────────────────────────────────────────────────

interface Props {
  discrepancies: BundleDiscrepancy[];
  onUserClick: (userId: string) => void;
}

export function BundleDiscrepancyTable({ discrepancies, onUserClick }: Props) {
  // Selecting rows only leads to a fix, so view-only gets no checkbox column.
  const canWrite = useHasAccess("write");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  // Maps bundleUsageId → Set of selected session IDs to link
  const [sessionSelections, setSessionSelections] = useState<
    Record<string, Set<string>>
  >({});
  // Tracks which over-recorded bundle usages have opted to reduce utilized count
  const [reduceUtilizedIds, setReduceUtilizedIds] = useState<Set<string>>(new Set());
  const [expandedIncorrectIds, setExpandedIncorrectIds] = useState<Set<string>>(
    new Set()
  );

  const { mutate: fixDiscrepancies, isPending: isFixing } =
    useFixBundleDiscrepancies();

  // Reset selection when the list refreshes
  useEffect(() => {
    setSelectedIds(new Set());
    setExpandedIncorrectIds(new Set());
  }, [discrepancies]);

  const toggleIncorrectExpanded = (id: string) => {
    setExpandedIncorrectIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const fixableRows = discrepancies.filter((r) => isFixable(r));
  const allFixableSelected =
    fixableRows.length > 0 &&
    fixableRows.every((r) => selectedIds.has(r.id));
  const someSelected = selectedIds.size > 0;

  const toggle = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (allFixableSelected) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(fixableRows.map((r) => r.id)));
    }
  };

  const toggleSession = (bundleUsageId: string, sessionId: string) => {
    setSessionSelections((prev) => {
      const current = new Set(prev[bundleUsageId] ?? []);
      current.has(sessionId) ? current.delete(sessionId) : current.add(sessionId);
      return { ...prev, [bundleUsageId]: current };
    });
  };

  const setSessionsForBundle = (bundleUsageId: string, ids: string[]) => {
    setSessionSelections((prev) => ({
      ...prev,
      [bundleUsageId]: new Set(ids),
    }));
  };

  // Compute what will be fixed vs still needs manual work
  const selectedRows = discrepancies.filter((r) => selectedIds.has(r.id));

  const toFix: { row: BundleDiscrepancy; newStatus?: string; newCount?: number }[] = [];
  const overRecordedRows: { row: BundleDiscrepancy }[] = [];
  const completedEarlyRows: { row: BundleDiscrepancy }[] = [];

  for (const row of selectedRows) {
    const newStatus = resolveFixStatus(row.discrepancy_flags);
    const newCount  = resolveSessionCountFix(row);

    if (newStatus) toFix.push({ row, newStatus });
    if (newCount !== null) toFix.push({ row, newCount });

    if (row.discrepancy_flags.includes("SESSION_COUNT_OVER_RECORDED")) {
      overRecordedRows.push({ row });
    }

    if (row.discrepancy_flags.includes("COMPLETED_WITH_REMAINING")) {
      completedEarlyRows.push({ row });
    }
  }

  const handleConfirmFix = () => {
    const statusAndCountFixes: FixItem[] = toFix.map(({ row, newStatus, newCount }) => ({
      usage_id: row.id,
      ...(newStatus && { new_status: newStatus as "ACTIVE" | "COMPLETED" | "EXPIRED" }),
      ...(newCount !== undefined && { new_utilized_session_quantity: newCount }),
    }));

    // Over-recorded rows: link sessions + optional status change + optional utilized reduction
    const overRecordedSessionFixes: FixItem[] = overRecordedRows.map(({ row }) => {
      const ids = [...(sessionSelections[row.id] ?? [])];
      const { postActual, newStatus } = resolveOverRecordedFix(row, ids.length);
      const shouldReduce = reduceUtilizedIds.has(row.id);
      return {
        usage_id: row.id,
        ...(newStatus && { new_status: newStatus }),
        ...(ids.length > 0 && { session_ids_to_link: ids }),
        ...(shouldReduce && { new_utilized_session_quantity: postActual }),
      };
    }).filter((f) => f.session_ids_to_link?.length || f.new_status || f.new_utilized_session_quantity !== undefined);

    // Completed-early rows: one combined FixItem per row with status + new utilized + session links
    const completedEarlyFixes: FixItem[] = completedEarlyRows.map(({ row }) => {
      const ids = [...(sessionSelections[row.id] ?? [])];
      const { newUtilized, newStatus } = resolveCompletedEarlyFix(row, ids.length);
      return {
        usage_id: row.id,
        new_status: newStatus,
        new_utilized_session_quantity: newUtilized,
        ...(ids.length > 0 && { session_ids_to_link: ids }),
      };
    });

    const fixes = [...statusAndCountFixes, ...overRecordedSessionFixes, ...completedEarlyFixes];

    if (fixes.length === 0) {
      setIsConfirmOpen(false);
      return;
    }

    fixDiscrepancies(fixes, {
      onSuccess: () => {
        setIsConfirmOpen(false);
        setSelectedIds(new Set());
        setSessionSelections({});
        setReduceUtilizedIds(new Set());
      },
    });
  };

  const openConfirmDialog = () => {
    setSessionSelections({});
    setReduceUtilizedIds(new Set());
    setIsConfirmOpen(true);
  };

  return (
    <TooltipProvider delayDuration={200}>
      {/* Subheader */}
      {someSelected && (
        <div className="flex items-center gap-3 px-2 py-2.5 mb-2 rounded-md bg-gray-50 border border-gray-200">
          <span className="text-xs font-medium text-gray-600">
            {selectedIds.size} selected
          </span>
          <button
            onClick={toggleAll}
            className="text-xs text-blue-600 hover:underline"
          >
            {allFixableSelected ? "Deselect all" : "Select all"}
          </button>
          <div className="ml-auto">
            <HasAccess permission="write">
            <Button
              size="sm"
              onClick={openConfirmDialog}
              disabled={isFixing}
            >
              Fix
            </Button>
            </HasAccess>
          </div>
        </div>
      )}

      <Table>
        <TableHeader>
          <TableRow>
            {canWrite && (
              <TableHead className="w-10">
                <Checkbox
                  checked={allFixableSelected}
                  onCheckedChange={toggleAll}
                  disabled={fixableRows.length === 0}
                />
              </TableHead>
            )}
            <TableHead>Bundle</TableHead>
            <TableHead>User(s)</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-center">Sessions (used / total)</TableHead>
            <TableHead className="text-center">Actual sessions Mapped</TableHead>
            <TableHead>End Date</TableHead>
            <TableHead>Discrepancies</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {discrepancies.map((row) => {
            const isPastEndDate = new Date(row.end_date) < new Date();
            const fixable = isFixable(row);
            const isSelected = selectedIds.has(row.id);
            const hasIncorrectMapping = row.discrepancy_flags.includes(
              "INCORRECT_SESSION_BUNDLE_MAPPED"
            );
            const isIncorrectExpanded = expandedIncorrectIds.has(row.id);

            return (
              <Fragment key={row.id}>
              <TableRow
                className={cn(isSelected && "bg-blue-50/50")}
              >
                {/* Checkbox */}
                {canWrite && (
                <TableCell>
                  {fixable ? (
                    <Checkbox
                      checked={isSelected}
                      onCheckedChange={() => toggle(row.id)}
                    />
                  ) : (
                    <Tooltip>
                      <TooltipTrigger asChild>
                          <Checkbox disabled />
                      </TooltipTrigger>
                      <TooltipContent>
                        No auto-fix available — manual intervention required
                      </TooltipContent>
                    </Tooltip>
                  )}
                </TableCell>
                )}

                <TableCell className="font-medium max-w-[180px] truncate">
                  {row.bundle_name}
                </TableCell>

                <TableCell className="max-w-[160px]">
                  {row.users.length === 0 ? (
                    <span className="text-muted-foreground text-xs italic">
                      No user linked
                    </span>
                  ) : (
                    <div className="flex flex-col gap-0.5">
                      {row.users.map((u) => (
                        <button
                          key={u.id}
                          onClick={() => onUserClick(u.id)}
                          className="text-xs text-left truncate text-blue-600 hover:underline hover:text-blue-800 transition-colors"
                        >
                          {u.full_name}
                        </button>
                      ))}
                    </div>
                  )}
                </TableCell>

                <TableCell>
                  <Badge variant="outline" className="text-xs">
                    {row.status}
                  </Badge>
                </TableCell>

                <TableCell className="text-center tabular-nums">
                  <span
                    className={cn(
                      "font-medium",
                      row.utilized_session_quantity >
                        row.total_session_quantity && "text-orange-600"
                    )}
                  >
                    {row.utilized_session_quantity}
                  </span>
                  <span className="text-muted-foreground">
                    {" "}
                    / {row.total_session_quantity}
                  </span>
                </TableCell>

                <TableCell className="text-center tabular-nums">
                  <span
                    className={cn(
                      row.actual_session_count !==
                        row.utilized_session_quantity &&
                        "text-blue-600 font-medium"
                    )}
                  >
                    {row.actual_session_count}
                  </span>
                </TableCell>

                <TableCell>
                  <span
                    className={cn(
                      "text-sm",
                      isPastEndDate &&
                        row.status === "ACTIVE" &&
                        "text-red-600 font-medium"
                    )}
                  >
                    {format(new Date(row.end_date), "dd MMM yyyy")}
                  </span>
                </TableCell>

                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {row.discrepancy_flags.map((flag) =>
                      flag === "INCORRECT_SESSION_BUNDLE_MAPPED" ? (
                        <button
                          key={flag}
                          type="button"
                          onClick={() => toggleIncorrectExpanded(row.id)}
                          className="inline-flex"
                        >
                          <Badge
                            variant="outline"
                            className={cn(
                              "text-xs cursor-pointer hover:opacity-80",
                              FLAG_CONFIG[flag].className,
                              isIncorrectExpanded && "ring-1 ring-rose-400"
                            )}
                          >
                            {FLAG_CONFIG[flag].label}
                            {isIncorrectExpanded ? " ▾" : " ▸"}
                          </Badge>
                        </button>
                      ) : (
                        <Badge
                          key={flag}
                          variant="outline"
                          className={cn(
                            "text-xs",
                            FLAG_CONFIG[flag].className
                          )}
                        >
                          {FLAG_CONFIG[flag].label}
                        </Badge>
                      )
                    )}
                  </div>
                </TableCell>
              </TableRow>

              {hasIncorrectMapping && isIncorrectExpanded && (
                <TableRow className="bg-rose-50/30 hover:bg-rose-50/30">
                  <TableCell colSpan={canWrite ? 8 : 7} className="p-0">
                    <div className="border-t border-rose-200">
                      <div className="px-3 py-2 bg-rose-50 border-b border-rose-100">
                        <span className="text-xs font-medium text-rose-900">
                          Incorrectly mapped sessions
                        </span>
                      </div>
                      <IncorrectMappedSessionsPanel bundleUsageId={row.id} />
                    </div>
                  </TableCell>
                </TableRow>
              )}
              </Fragment>
            );
          })}
        </TableBody>
      </Table>

      {/* Confirmation dialog */}
      <Dialog open={isConfirmOpen} onOpenChange={setIsConfirmOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Confirm Fix</DialogTitle>
          </DialogHeader>

          <div className="space-y-4 text-sm text-gray-600">
            {/* Status / count fixes */}
            {toFix.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1.5">
                  Auto-fix ({toFix.length}):
                </p>
                <div className="rounded-md border border-gray-200 divide-y divide-gray-100 max-h-[200px] overflow-y-auto">
                  {toFix.map(({ row, newStatus, newCount }, i) => (
                    <div
                      key={`${row.id}-${i}`}
                      className="flex items-center justify-between px-3 py-2 gap-2"
                    >
                      <span className="text-xs font-medium text-gray-800 truncate">
                        {row.bundle_name}
                      </span>
                      {newStatus && (
                        <Badge
                          variant="outline"
                          className="text-xs shrink-0 bg-green-50 text-green-800 border-green-200"
                        >
                          → {newStatus}
                        </Badge>
                      )}
                      {newCount !== undefined && (
                        <Badge
                          variant="outline"
                          className="text-xs shrink-0 bg-blue-50 text-blue-800 border-blue-200"
                        >
                          Sync count → {newCount}
                        </Badge>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Session-count-over-recorded: orphaned session linker */}
            {overRecordedRows.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-1">
                  Session Count High — select orphaned sessions to link:
                </p>
                <p className="text-xs text-muted-foreground mb-2">
                  Recorded count exceeds actual linked sessions. Select matching sessions below to re-link them to this bundle.
                </p>
                <div className="space-y-2">
                  {overRecordedRows.map(({ row }) => (
                    <OrphanedSessionAccordion
                      key={row.id}
                      row={row}
                      selectedSessionIds={sessionSelections[row.id] ?? new Set()}
                      onToggleSession={(sid) => toggleSession(row.id, sid)}
                      onSetSessions={(ids) => setSessionsForBundle(row.id, ids)}
                      reduceUtilized={reduceUtilizedIds.has(row.id)}
                      onToggleReduceUtilized={() =>
                        setReduceUtilizedIds((prev) => {
                          const next = new Set(prev);
                          next.has(row.id) ? next.delete(row.id) : next.add(row.id);
                          return next;
                        })
                      }
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Completed early: date-scoped session mapper + reopen */}
            {completedEarlyRows.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-700 mb-2">
                  Completed Early — map sessions &amp; reopen:
                </p>
                <div className="space-y-2">
                  {completedEarlyRows.map(({ row }) => (
                    <CompletedEarlySessionAccordion
                      key={row.id}
                      row={row}
                      selectedSessionIds={sessionSelections[row.id] ?? new Set()}
                      onToggleSession={(sid) => toggleSession(row.id, sid)}
                      onSetSessions={(ids) => setSessionsForBundle(row.id, ids)}
                    />
                  ))}
                </div>
              </div>
            )}

            <p className="text-amber-700 font-medium text-xs">
              This action cannot be undone.
            </p>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setIsConfirmOpen(false)}
              disabled={isFixing}
            >
              Cancel
            </Button>
            <Button onClick={handleConfirmFix} disabled={isFixing}>
              {isFixing ? "Fixing…" : "Confirm Fix"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </TooltipProvider>
  );
}
